import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

function buildAbsoluteUrl(imageUrl) {
  if (!imageUrl) return '';
  try {
    if (typeof window !== 'undefined') {
      return new URL(imageUrl, window.location.origin).toString();
    }
  } catch (err) {
    console.warn('Failed to build absolute URL for image', err);
  }
  return imageUrl;
}

export default function ManageOEMImageGallery({
  open,
  onClose,
  images = [],
  oemNumber,
  busy = false,
  onMakePrimary,
  onDelete,
  onReorder,
  onRefresh,
  initialImageKey,
  canEdit = true
}) {
  const [order, setOrder] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [message, setMessage] = useState('');
  const readOnly = !canEdit;
  const MIN_ZOOM = 0.5;
  const MAX_ZOOM = 3;
  const ZOOM_STEP = 0.25;
  const WHEEL_ZOOM_STEP = 0.15;
  const [zoom, setZoom] = useState(1);
  const previewRef = useRef(null);
  const dragStateRef = useRef({ isDragging: false, startX: 0, startY: 0, scrollLeft: 0, scrollTop: 0 });

  const [isDragging, setIsDragging] = useState(false);

  const clampScale = useCallback((raw) => {
    const value = Number(raw);
    if (!Number.isFinite(value)) return 1;
    return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));
  }, [MAX_ZOOM, MIN_ZOOM]);

  useEffect(() => {
    const keys = images.map(img => img?.OEMImageKey).filter(Boolean);
    setOrder(keys);
    setSelectedKey(prev => {
      if (initialImageKey && keys.includes(initialImageKey)) {
        return initialImageKey;
      }
      if (prev && keys.includes(prev)) {
        return prev;
      }
      return keys[0] ?? null;
    });
  }, [images, initialImageKey]);

  useEffect(() => {
    setZoom(1);
    if (previewRef.current) {
      previewRef.current.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }, [selectedKey, open]);

  const stopDragging = useCallback(() => {
    if (!dragStateRef.current.isDragging) return;
    dragStateRef.current.isDragging = false;
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleWindowUp = () => stopDragging();
    window.addEventListener('mouseup', handleWindowUp);
    window.addEventListener('mouseleave', handleWindowUp);
    return () => {
      window.removeEventListener('mouseup', handleWindowUp);
      window.removeEventListener('mouseleave', handleWindowUp);
    };
  }, [stopDragging]);

  useEffect(() => {
    if (!message) return undefined;
    const handle = setTimeout(() => setMessage(''), 2000);
    return () => clearTimeout(handle);
  }, [message]);

  if (!open) return null;

  const orderedImages = useMemo(() => {
    const byKey = new Map(images.map(img => [img.OEMImageKey, img]));
    if (!order.length) return images;
    return order.map(id => byKey.get(id)).filter(Boolean);
  }, [images, order]);

  const selectedImage = useMemo(() => {
    if (!selectedKey) return orderedImages[0] || null;
    return orderedImages.find(img => img?.OEMImageKey === selectedKey) || orderedImages[0] || null;
  }, [orderedImages, selectedKey]);


  const setPrimary = useCallback(async (image) => {
    if (readOnly) {
      setMessage('Viewer access: cannot change default image.');
      return;
    }
    if (!image || typeof onMakePrimary !== 'function') return;
    const success = await onMakePrimary(image.OEMImageKey);
    if (success) {
      setSelectedKey(image.OEMImageKey);
      setMessage('Primary image updated.');
    }
  }, [readOnly, onMakePrimary]);

  const removeImage = useCallback(async (image) => {
    if (readOnly) {
      setMessage('Viewer access: cannot delete images.');
      return;
    }
    if (!image || typeof onDelete !== 'function') return;
    if (typeof window !== 'undefined' && !window.confirm('Delete this image? This cannot be undone.')) {
      return;
    }
    const success = await onDelete(image.OEMImageKey);
    if (success) {
      setMessage('Image deleted.');
    }
  }, [readOnly, onDelete]);

  const moveImage = useCallback(async (index, delta) => {
    if (readOnly) {
      setMessage('Viewer access: cannot reorder images.');
      return;
    }
    if (typeof onReorder !== 'function') return;
    const nextIndex = index + delta;
    if (nextIndex < 0 || nextIndex >= order.length) return;
    const baseline = [...order];
    const next = [...order];
    [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
    setOrder(next);
    const success = await onReorder(next);
    if (!success) {
      setOrder(baseline);
    } else {
      setSelectedKey(next[nextIndex]);
      setMessage('Image order updated.');
    }
  }, [readOnly, onReorder, order]);

  const copyUrl = useCallback((image) => {
    if (!image) return;
    const absoluteUrl = buildAbsoluteUrl(image.imageUrl);
    if (!absoluteUrl) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(absoluteUrl).then(() => {
        setMessage('Copied image URL to clipboard.');
      }).catch(() => {
        setMessage('Copy failed.');
      });
    } else if (typeof window !== 'undefined') {
      window.prompt('Image URL', absoluteUrl);
    }
  }, []);

  const openFull = useCallback((image) => {
    if (!image?.imageUrl) return;
    const absoluteUrl = buildAbsoluteUrl(image.imageUrl);
    if (typeof window !== 'undefined') {
      window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
    }
  }, []);

  const refresh = useCallback(() => {
    if (typeof onRefresh === 'function') {
      onRefresh();
    }
  }, [onRefresh]);

  const zoomPercent = useMemo(() => Math.round(zoom * 100), [zoom]);

  const setZoomSafely = useCallback((valueOrUpdater) => {
    setZoom(prev => {
      const raw = typeof valueOrUpdater === 'function' ? valueOrUpdater(prev) : valueOrUpdater;
      const clamped = Number(clampScale(raw).toFixed(2));
      return clamped;
    });
  }, [clampScale]);

  const handleZoomIn = useCallback(() => {
    setZoomSafely(prev => prev + ZOOM_STEP);
  }, [setZoomSafely, ZOOM_STEP]);

  const handleZoomOut = useCallback(() => {
    setZoomSafely(prev => prev - ZOOM_STEP);
  }, [setZoomSafely, ZOOM_STEP]);

  const handleZoomReset = useCallback(() => {
    setZoomSafely(1);
    const container = previewRef.current;
    if (container) {
      container.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }, [setZoomSafely]);

  const handleZoomSlider = useCallback((event) => {
    const value = parseFloat(event?.target?.value);
    if (!Number.isFinite(value)) return;
    setZoomSafely(value);
  }, [setZoomSafely]);

  const handlePreviewMouseDown = useCallback((event) => {
    if (event.button !== 0) return;
    if (!previewRef.current || !selectedImage || zoom <= 1) return;
    event.preventDefault();
    const container = previewRef.current;
    dragStateRef.current = {
      ...dragStateRef.current,
      isDragging: true,
      startX: event.clientX,
      startY: event.clientY,
      scrollLeft: container.scrollLeft,
      scrollTop: container.scrollTop
    };
    setIsDragging(true);
  }, [selectedImage, zoom]);

  const handlePreviewMouseMove = useCallback((event) => {
    if (!dragStateRef.current.isDragging || !previewRef.current) return;
    event.preventDefault();
    const container = previewRef.current;
    const dx = event.clientX - dragStateRef.current.startX;
    const dy = event.clientY - dragStateRef.current.startY;
    container.scrollLeft = dragStateRef.current.scrollLeft - dx;
    container.scrollTop = dragStateRef.current.scrollTop - dy;
  }, []);

  const handlePreviewMouseUp = useCallback(() => {
    stopDragging();
  }, [stopDragging]);

  const handleWheelZoom = useCallback((event) => {
    if (!selectedImage || !previewRef.current) return;
    event.preventDefault();
    const container = previewRef.current;
    const delta = event.deltaY;
    if (delta === 0) return;
    const direction = delta < 0 ? 1 : -1;
    const proposed = zoom + direction * WHEEL_ZOOM_STEP;
    const clamped = Number(clampScale(proposed).toFixed(2));
    if (clamped === zoom) return;

    const rect = container.getBoundingClientRect();
    const pointerX = event.clientX - rect.left;
    const pointerY = event.clientY - rect.top;
    const contentX = container.scrollLeft + pointerX;
    const contentY = container.scrollTop + pointerY;
    const scaleRatio = clamped / zoom;

    setZoomSafely(clamped);

    const newScrollLeft = contentX * scaleRatio - pointerX;
    const newScrollTop = contentY * scaleRatio - pointerY;
    container.scrollLeft = newScrollLeft;
    container.scrollTop = newScrollTop;
  }, [selectedImage, zoom, WHEEL_ZOOM_STEP, clampScale, setZoomSafely]);

  return (
    <div className="pim-gallery-overlay">
      <div className="pim-gallery-panel">
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid var(--modal-border-gray)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <div style={{ fontWeight: 600 }}>
            OEM Image Gallery{ oemNumber ? ` – ${oemNumber}` : '' }
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={refresh}
              disabled={busy}
              style={{
                background: 'var(--modal-bg-light-blue)', color: 'var(--modal-text-dark)',
                padding: '8px 12px', borderRadius: '8px', border: '0px'
              }}
            >Refresh</button>
            <button
              onClick={onClose}
              style={{
                background: 'var(--modal-bg-blue)', color: 'var(--modal-text-white)',
                padding: '8px 12px', borderRadius: '8px', border: '0px'
              }}
            >Close</button>
          </div>
        </div>
        <div style={{
          padding: '20px', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gap: '16px',
          overflow: 'hidden'
        }}>
          <div style={{
            border: '1px solid var(--modal-border-gray)', borderRadius: '12px', padding: '16px',
            display: 'grid', gridTemplateRows: 'auto 1fr auto auto auto', gap: '12px', background: '#f8fafc',
            minHeight: 0
          }}>
            <div style={{ fontWeight: 600 }}>Preview</div>
            <div
              ref={previewRef}
              onMouseDown={handlePreviewMouseDown}
              onMouseMove={handlePreviewMouseMove}
              onMouseUp={handlePreviewMouseUp}
              onMouseLeave={handlePreviewMouseUp}
              onWheel={handleWheelZoom}
              style={{
                position: 'relative',
                border: '1px dashed var(--modal-border-gray)',
                borderRadius: '10px',
                background: '#fff',
                overflow: 'auto',
                minHeight: 0,
                padding: '12px',
                cursor: selectedImage && zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
              }}
            >
              {selectedImage ? (
                <img
                  src={selectedImage.imageUrl}
                  alt={selectedImage.imagePath || 'OEM image'}
                  draggable={false}
                  style={{
                    display: 'block',
                    width: `${zoom * 100}%`,
                    height: 'auto',
                    maxWidth: 'none',
                    userSelect: 'none',
                    pointerEvents: 'none',
                    minHeight: '280px'
                  }}
                />
              ) : (
                <div style={{ color: 'var(--modal-text-gray)', fontSize: '13px' }}>No image selected</div>
              )}
            </div>
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '12px', color: 'var(--modal-text-gray)' }}>Zoom</span>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button
                    type="button"
                    onClick={handleZoomOut}
                    disabled={!selectedImage || zoom <= MIN_ZOOM || busy}
                    style={{
                      background: 'var(--modal-bg-light-blue)', color: 'var(--modal-text-dark)',
                      padding: '4px 10px', borderRadius: '6px', border: '0px'
                    }}
                  >-</button>
                  <button
                    type="button"
                    onClick={handleZoomReset}
                    disabled={!selectedImage || zoom === 1 || busy}
                    style={{
                      background: 'var(--modal-bg-light-blue)', color: 'var(--modal-text-dark)',
                      padding: '4px 10px', borderRadius: '6px', border: '0px'
                    }}
                  >Reset</button>
                  <button
                    type="button"
                    onClick={handleZoomIn}
                    disabled={!selectedImage || zoom >= MAX_ZOOM || busy}
                    style={{
                      background: 'var(--modal-bg-light-blue)', color: 'var(--modal-text-dark)',
                      padding: '4px 10px', borderRadius: '6px', border: '0px'
                    }}
                  >+</button>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 160px', maxWidth: '320px' }}>
                <input
                  type="range"
                  min={MIN_ZOOM}
                  max={MAX_ZOOM}
                  step={0.1}
                  value={zoom}
                  onChange={handleZoomSlider}
                  disabled={!selectedImage}
                  style={{ flex: '1 1 auto' }}
                />
                <span style={{ fontSize: '12px', color: 'var(--modal-text-gray)', width: '48px', textAlign: 'right' }}>
                  {zoomPercent}%
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                onClick={() => copyUrl(selectedImage)}
                disabled={!selectedImage || busy}
                style={{
                  background: 'var(--modal-bg-light-blue)', color: 'var(--modal-text-dark)',
                  padding: '6px 12px', borderRadius: '6px', border: '0px'
                }}
              >Copy URL</button>
              <button
                onClick={() => openFull(selectedImage)}
                disabled={!selectedImage}
                style={{
                  background: 'var(--modal-bg-light-blue)', color: 'var(--modal-text-dark)',
                  padding: '6px 12px', borderRadius: '6px', border: '0px'
                }}
              >Open Fullscreen</button>
              <button
                onClick={() => setPrimary(selectedImage)}
                disabled={readOnly || !selectedImage || busy || selectedImage?.isPrimary}
                style={{
                  background: 'var(--modal-bg-blue)', color: 'var(--modal-text-white)',
                  padding: '6px 12px', borderRadius: '6px', border: '0px'
                }}
              >Set Default</button>
              <button
                onClick={() => removeImage(selectedImage)}
                disabled={readOnly || !selectedImage || busy}
                style={{
                  background: 'var(--danger)', color: 'var(--modal-text-white)',
                  padding: '6px 12px', borderRadius: '6px', border: '0px'
                }}
              >Delete</button>
            </div>
            {selectedImage && (
              <div style={{ fontSize: '12px', color: 'var(--modal-text-gray)', lineHeight: 1.4 }}>
                <div><strong>Stored path:</strong> {selectedImage.imagePath || '—'}</div>
                <div><strong>Source URL:</strong> {selectedImage.sourceUrl || '—'}</div>
                <div><strong>Created:</strong> {selectedImage.createdAt ? new Date(selectedImage.createdAt).toLocaleString() : '—'}</div>
              </div>
            )}
          </div>
          <div style={{
            overflowY: 'auto', display: 'grid', gap: '12px', alignContent: orderedImages.length ? 'start' : 'center'
          }}>
            {orderedImages.length ? orderedImages.map((img, index) => (
              <div
                key={img.OEMImageKey}
                style={{
                  border: '1px solid var(--modal-border-gray)', borderRadius: '10px', padding: '12px',
                  background: '#ffffff', display: 'grid', gap: '8px'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontWeight: 600 }}>
                    #{index + 1}{img.isPrimary ? ' · Default' : ''}
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      onClick={() => moveImage(index, -1)}
                      disabled={readOnly || busy || index === 0}
                      style={{
                        background: 'var(--modal-bg-light-blue)', color: 'var(--modal-text-dark)',
                        padding: '4px 8px', borderRadius: '6px', border: '0px'
                      }}
                    >↑</button>
                    <button
                      onClick={() => moveImage(index, 1)}
                      disabled={readOnly || busy || index === orderedImages.length - 1}
                      style={{
                        background: 'var(--modal-bg-light-blue)', color: 'var(--modal-text-dark)',
                        padding: '4px 8px', borderRadius: '6px', border: '0px'
                      }}
                    >↓</button>
                    <button
                      onClick={() => setSelectedKey(img.OEMImageKey)}
                      disabled={selectedKey === img.OEMImageKey}
                      style={{
                        background: selectedKey === img.OEMImageKey ? 'var(--modal-bg-blue)' : 'var(--modal-bg-light-blue)',
                        color: selectedKey === img.OEMImageKey ? 'var(--modal-text-white)' : 'var(--modal-text-dark)',
                        padding: '4px 8px', borderRadius: '6px', border: '0px'
                      }}
                    >Preview</button>
                  </div>
                </div>
                <div style={{ fontSize: '12px', color: 'var(--modal-text-gray)' }}>
                  {img.imagePath || '—'}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--modal-text-gray)' }}>
                  Source: {img.sourceUrl || '—'}
                </div>
              </div>
            )) : (
              <div style={{
                border: '1px dashed var(--modal-border-gray)', borderRadius: '10px', padding: '16px',
                textAlign: 'center', color: 'var(--modal-text-gray)'
              }}>
                No images captured for this OEM yet. Use "Capture" inside the Manage OEM modal to add one.
              </div>
            )}
          </div>
        </div>
        <div style={{
          padding: '12px 20px', borderTop: '1px solid var(--modal-border-gray)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <div style={{ fontSize: '12px', color: 'var(--modal-text-gray)' }}>
            {orderedImages.length} / 8 images stored. Arrow controls update SortOrder instantly.
          </div>
          {message && <div style={{ fontSize: '12px', color: 'var(--modal-text-dark)' }}>{message}</div>}
        </div>
      </div>
    </div>
  );
}
